/**
 * Error Type Definitions
 * Comprehensive type safety for error handling throughout the application
 */

import { ERROR_TYPES, BACKEND_STATUS, CRITICAL_ERROR_TYPES } from '../constants';

/**
 * Error severity levels
 */
export type ErrorType = typeof ERROR_TYPES[keyof typeof ERROR_TYPES];
export type BackendStatus = typeof BACKEND_STATUS[keyof typeof BACKEND_STATUS];
export type CriticalErrorType = typeof CRITICAL_ERROR_TYPES[keyof typeof CRITICAL_ERROR_TYPES] | null;

/**
 * Application error with structured data
 */
export interface AppError {
  /** Unique error identifier */
  id: string;

  /** Error type for classification */
  type: ErrorType;

  /** Human-readable error message */
  message: string;

  /** HTTP status code if applicable */
  status?: number;

  /** Application-specific error code */
  code?: string;

  /** Timestamp when error occurred */
  timestamp: number;

  /** Context where error occurred (e.g., 'login', 'document_upload') */
  context?: string;

  /** Whether error can be retried */
  retryable?: boolean;

  /** Additional error details */
  details?: unknown;
}

/**
 * API error response structure
 */
export interface ApiErrorResponse {
  message: string;
  code?: string;
  status: number;
  details?: Record<string, unknown>;
}

/**
 * API error with response data
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly response?: ApiErrorResponse;

  constructor(message: string, status: number, code?: string, response?: ApiErrorResponse) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.response = response;

    // Maintains proper stack trace for where error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Check if error is a specific HTTP status
   */
  isStatus(status: number): boolean {
    return this.status === status;
  }

  /**
   * Check if error is unauthorized (401)
   */
  isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Check if error is forbidden (403)
   */
  isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Check if error is validation error (422)
   */
  isValidationError(): boolean {
    return this.status === 422;
  }

  /**
   * Check if error is server error (5xx)
   */
  isServerError(): boolean {
    return this.status >= 500 && this.status < 600;
  }

  /**
   * Check if error is retryable
   */
  isRetryable(): boolean {
    return this.isServerError() || this.status === 429; // Too Many Requests
  }
}

/**
 * Network error (no response from server)
 */
export class NetworkError extends Error {
  public readonly originalError: unknown;

  constructor(message: string, originalError?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.originalError = originalError;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError);
    }
  }

  /**
   * Check if this is a timeout error
   */
  isTimeout(): boolean {
    const message = this.message.toLowerCase();
    return message.includes('timeout') || message.includes('aborted');
  }

  /**
   * Network errors are always retryable
   */
  isRetryable(): boolean {
    return true;
  }
}

/**
 * Unauthorized error (401)
 */
export class UnauthorizedError extends ApiError {
  constructor(message: string = 'Session expired. Please log in again.', code?: string) {
    super(message, 401, code);
    this.name = 'UnauthorizedError';
  }
}

/**
 * Validation error (422)
 */
export class ValidationError extends ApiError {
  public readonly validationErrors?: Record<string, string[]>;

  constructor(
    message: string,
    validationErrors?: Record<string, string[]>,
    response?: ApiErrorResponse
  ) {
    super(message, 422, 'VALIDATION_ERROR', response);
    this.name = 'ValidationError';
    this.validationErrors = validationErrors;
  }

  /**
   * Get validation errors for a specific field
   */
  getFieldErrors(field: string): string[] {
    return this.validationErrors?.[field] || [];
  }

  /**
   * Check if field has validation errors
   */
  hasFieldErrors(field: string): boolean {
    return this.getFieldErrors(field).length > 0;
  }
}

/**
 * Type guard to check if error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Type guard to check if error is a NetworkError
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Type guard to check if error is an UnauthorizedError
 */
export function isUnauthorizedError(error: unknown): error is UnauthorizedError {
  return error instanceof UnauthorizedError;
}

/**
 * Type guard to check if error is a ValidationError
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Type guard to check if error has an HTTP response
 */
export function hasHttpResponse(error: unknown): error is { response: { status: number; data?: any } } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as any).response === 'object' &&
    'status' in (error as any).response
  );
}

/**
 * Type guard to check if error is a fetch TypeError (network error)
 */
export function isFetchError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    (error.message.includes('fetch') || error.message.includes('network'))
  );
}

/**
 * Extract error message from unknown error type
 */
export function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (hasHttpResponse(error) && error.response.data?.message) {
    return error.response.data.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Extract status code from error
 */
export function extractStatusCode(error: unknown): number | undefined {
  if (isApiError(error)) {
    return error.status;
  }

  if (hasHttpResponse(error)) {
    return error.response.status;
  }

  return undefined;
}
